/**
 * The silicone pad, computed once at module load (pure path strings).
 *
 * A broad rounded dome — local origin at its top centre, height 100, radius
 * 72, a horizontal tangent at the apex (a radiused sole, never a point) —
 * shaded as cel bands that follow the silhouette: a lit crescent on the left
 * flank, a shade crescent and a core sliver on the right. The ink on its tip
 * is drawn as the mirrored mark's cross-section. Pressed, the pad spreads
 * into a bulge ring (flat on the cliché, following the crown on the cap).
 */

const LEFT = [
  [-72, 0],
  [-73, 50],
  [-42, 100],
  [0, 100],
]

const r1 = (n) => {
  const v = Math.round(n * 10) / 10
  return Object.is(v, -0) ? 0 : v
}
const pt = ([x, y]) => `${r1(x)} ${r1(y)}`
const curve = ([, b, c, d]) => `C${pt(b)} ${pt(c)} ${pt(d)}`
const polyline = (points) => points.map(pt).join('L')
const mirror = (pts) => pts.map(([x, y]) => [-x, y])
const reverse = (pts) => [...pts].reverse()

const bez = ([a, b, c, d], t) => {
  const u = 1 - t
  return [0, 1].map((i) => u * u * u * a[i] + 3 * u * u * t * b[i] + 3 * u * t * t * c[i] + t * t * t * d[i])
}
const mix = (p, q, t) => [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]

/** de Casteljau: the part of a cubic from 0 to t. */
function head([a, b, c, d], t) {
  const ab = mix(a, b, t)
  const bc = mix(b, c, t)
  const abc = mix(ab, bc, t)
  const bcd = mix(bc, mix(c, d, t), t)
  return [a, ab, abc, mix(abc, bcd, t)]
}

const RIGHT = reverse(mirror(LEFT)) // apex → right base
const samples = (n) => Array.from({ length: n + 1 }, (_, i) => i / n)

export const DOME = `M${pt(LEFT[0])}${curve(LEFT)}${curve(RIGHT)}Z`
export const RIM_LIT = `M${pt(LEFT[0])}${curve(LEFT)}`
export const RIM_BACK = `M${pt(RIGHT[0])}${curve(RIGHT)}`

/* Cel bands: the lit crescent ends before the apex (the sole faces down). */
const LIT_EDGE = head(LEFT, 0.8)
export const DOME_LIT = `M${pt(LEFT[0])}${curve(LIT_EDGE)}C-38 82 -57 46 -55 0Z`
export const DOME_SHADE = `M30 0C34 46 24 86 0 100${curve(RIGHT)}Z`
export const DOME_CORE = `M62 0C63 48 36 94 0 100${curve(RIGHT)}Z`
export const DOME_SPEC = 'M-66.5 9C-66 36 -56 62 -38 82'

/* The ink film on the tip, inset 1.5: the mirrored mark seen edge-on is a
   few short white bands where its strokes cross the pad's centre line. */
const MARK_SECTION = [-32, 10.4, 21.6, 32]
const TIP = samples(120)
  .flatMap((t) => [bez(LEFT, t), bez(mirror(LEFT), t)])
  .filter(([x]) => Math.abs(x) <= 36)
export const LIP = MARK_SECTION.map((xc) => {
  const dash = TIP.filter(([x]) => Math.abs(x - xc) <= 2.4)
    .map(([x, y]) => [x, y - 1.5])
    .sort((p, q) => p[0] - q[0])
  return `M${polyline(dash)}`
}).join('')

/* ---- The bulge: the squashed pad's equator ---------------------------------
   Pressed, the soft body spreads into a ring wider than the dome where it is
   cut by the surface. Seen from above, that ring's front half is the pad's
   bottom outline (the contact line itself is hidden under it) and its sides
   stand proud of the dome's flanks. Stage units about the ring centre; the
   scene scales it with the patch. Flat on the cliché; on the crowned cap the
   ring follows the crown, so its sides hang down over the barrel. */
const LIT_U = -50
const SHADE_U = 26
const CORE_DEPTH = 15
export const CROWN = 16

/* The ring sits a little off the surface, so on the crown it droops by part
   of the crown's fall, and it is a touch narrower than on the flat plate. */
const ring = (droop) => (u, v) => {
  const x = u - 0.18 * v
  return [x, 0.5 * v + droop * CROWN * (x / 70) ** 2]
}
const deg = (d) => (d * Math.PI) / 180
const arcPts = (map, rb, from, to, n, rv = rb) =>
  samples(n).map((k) => {
    const a = deg(from + (to - from) * k)
    return map(rb * Math.cos(a), rv * Math.sin(a))
  })

function bulgeShapes(rb, droop) {
  const map = ring(droop)
  const litA = (Math.acos(LIT_U / rb) * 180) / Math.PI
  const shadeA = (Math.acos(SHADE_U / rb) * 180) / Math.PI
  /* The diameter between u0 and u1, sampled so it bends with the crown. */
  const diameter = (u0, u1) => samples(12).map((k) => map(u0 + (u1 - u0) * k, 0))
  const front = arcPts(map, rb, 0, 180, 36)
  return {
    back: `M${polyline(arcPts(map, rb, 0, 360, 72))}Z`,
    backLit: `M${polyline(arcPts(map, rb, litA, 360 - litA, 24))}Z`,
    front: `M${polyline([...front, ...diameter(-rb, rb)])}Z`,
    frontLit: `M${polyline([...arcPts(map, rb, litA, 180, 12), ...diameter(-rb, LIT_U)])}Z`,
    frontShade: `M${polyline([...arcPts(map, rb, 0, shadeA, 14), ...diameter(SHADE_U, rb)])}Z`,
    core: `M${polyline([...front, ...reverse(arcPts(map, rb, 0, 180, 36, rb - CORE_DEPTH))])}Z`,
    rim: `M${polyline(front)}`,
    seam: `M${polyline(diameter(-rb, rb))}`,
  }
}
export const BULGE_FLAT = bulgeShapes(78, 0)
export const BULGE_ARCH = bulgeShapes(72, 0.6)
